// =============================================================================
// MeuDriver — Student Onboarding Wizard (3 Steps)
// =============================================================================

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronRight, ChevronLeft, CheckCircle, MapPin, Target, GraduationCap, Car } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { BRAZILIAN_STATES } from '@/lib/constants'

// ─── Schemas ─────────────────────────────────────────────────────────────────

const step1Schema = z.object({
  full_name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  phone: z.string().optional(),
  city: z.string().min(2, 'Informe sua cidade'),
  state: z.string().length(2, 'Selecione o estado'),
})

type Step1Data = z.infer<typeof step1Schema>

// ─── Constants ────────────────────────────────────────────────────────────────

const GOALS = [
  { key: 'get_cnh',   label: 'Tirar a CNH',             icon: '🚗', desc: 'Primeira habilitação' },
  { key: 'improve',   label: 'Melhorar a direção',       icon: '📈', desc: 'Já tenho CNH mas quero praticar' },
  { key: 'defensive', label: 'Direção defensiva',        icon: '🛡️', desc: 'Técnicas de segurança' },
  { key: 'manual',    label: 'Aprender câmbio manual',   icon: '⚙️', desc: 'Dominar o câmbio manual' },
  { key: 'parking',   label: 'Estacionamento',           icon: '🅿️', desc: 'Manobras e estacionamento' },
  { key: 'highway',   label: 'Rodovias e estradas',      icon: '🛣️', desc: 'Direção em estradas' },
]

const LEVELS = [
  { key: 'beginner',     label: 'Iniciante',      icon: '🌱', desc: 'Nunca ou raramente dirigi' },
  { key: 'intermediate', label: 'Intermediário',  icon: '🌿', desc: 'Tenho alguma experiência' },
  { key: 'advanced',     label: 'Experiente',     icon: '🌳', desc: 'Dirijo há bastante tempo' },
]

const TOTAL_STEPS = 3

// ─── Progress component ───────────────────────────────────────────────────────

function WizardProgress({ step }: { step: number }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-brand-700">Passo {step} de {TOTAL_STEPS}</span>
        <span className="text-muted-foreground">{Math.round(((step - 1) / (TOTAL_STEPS - 1)) * 100)}% concluído</span>
      </div>
      <Progress value={((step - 1) / (TOTAL_STEPS - 1)) * 100} className="h-2" />
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function StudentOnboarding() {
  const { user, profile, refreshProfile } = useAuth()
  const navigate = useNavigate()

  const [step, setStep] = useState(1)
  const [selectedGoals, setSelectedGoals] = useState<Set<string>>(new Set())
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  const form1 = useForm<Step1Data>({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      full_name: profile?.full_name ?? '',
      phone: profile?.phone ?? '',
      city: profile?.city ?? '',
      state: profile?.state ?? '',
    },
  })

  // ── Navigation ──────────────────────────────────────────────────────────────

  async function handleNext() {
    if (step === 1) {
      const valid = await form1.trigger()
      if (!valid) return
      setStep(2)
    } else if (step === 2) {
      if (selectedGoals.size === 0) {
        toast.warning('Selecione pelo menos um objetivo.')
        return
      }
      setStep(3)
    } else if (step === 3) {
      await handleSubmit()
    }
  }

  // ── Submit ──────────────────────────────────────────────────────────────────

  async function handleSubmit() {
    if (!user?.id) return
    setSubmitting(true)
    try {
      const data = form1.getValues()
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: data.full_name,
          phone: data.phone || null,
          city: data.city,
          state: data.state,
        })
        .eq('id', user.id)

      if (error) throw error
      await refreshProfile()
      setDone(true)
    } catch {
      toast.error('Erro ao salvar. Tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Done screen ─────────────────────────────────────────────────────────────

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 to-white p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="flex justify-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-100">
              <CheckCircle className="h-10 w-10 text-brand-600" />
            </div>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tudo pronto!</h1>
            <p className="text-muted-foreground mt-2">
              Seu perfil foi configurado. Agora encontre um instrutor perfeito para você.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 text-left">
            {[
              { icon: '📍', text: `Cidade: ${form1.getValues('city')} — ${form1.getValues('state')}` },
              { icon: '🎯', text: `${selectedGoals.size} objetivo(s) selecionado(s)` },
              { icon: '📊', text: selectedLevel ? LEVELS.find(l => l.key === selectedLevel)?.label ?? '' : 'Nível não informado' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg bg-gray-50 px-4 py-3">
                <span className="text-lg">{item.icon}</span>
                <span className="text-sm text-gray-700">{item.text}</span>
              </div>
            ))}
          </div>
          <Button className="w-full" size="lg" onClick={() => navigate('/instrutores')}>
            Buscar instrutores
          </Button>
          <button
            onClick={() => navigate('/aluno/dashboard')}
            className="text-sm text-muted-foreground hover:text-gray-800 underline-offset-4 hover:underline"
          >
            Ir para o dashboard
          </button>
        </div>
      </div>
    )
  }

  const stepTitles = ['Suas informações', 'Seu objetivo', 'Seu nível']
  const stepIcons = [MapPin, Target, GraduationCap]
  const StepIcon = stepIcons[step - 1]

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-teal-50 flex items-start justify-center p-4 pt-10">
      <div className="w-full max-w-xl space-y-6">

        {/* Brand header */}
        <div className="flex items-center gap-2 justify-center">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 text-white">
            <Car className="h-4 w-4" />
          </div>
          <span className="font-bold text-xl text-brand-700">MeuDriver</span>
        </div>

        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Bem-vindo(a)!</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Vamos configurar seu perfil em poucos passos.
          </p>
        </div>

        <WizardProgress step={step} />

        <Card>
          <CardContent className="p-6">
            <h2 className="font-semibold text-lg mb-5 flex items-center gap-2">
              <StepIcon className="h-5 w-5 text-brand-600" />
              {stepTitles[step - 1]}
            </h2>

            {/* ── STEP 1: Personal info ──────────────────────────────── */}
            {step === 1 && (
              <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleNext() }}>
                <div className="space-y-1.5">
                  <Label htmlFor="full_name">Nome completo *</Label>
                  <Input id="full_name" placeholder="João da Silva" {...form1.register('full_name')} />
                  {form1.formState.errors.full_name && (
                    <p className="text-xs text-red-500">{form1.formState.errors.full_name.message}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="phone">Telefone / WhatsApp</Label>
                  <Input id="phone" type="tel" placeholder="(11) 9 9999-9999" {...form1.register('phone')} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="city">Cidade *</Label>
                    <Input id="city" placeholder="São Paulo" {...form1.register('city')} />
                    {form1.formState.errors.city && (
                      <p className="text-xs text-red-500">{form1.formState.errors.city.message}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="state">Estado *</Label>
                    <select
                      id="state"
                      {...form1.register('state')}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="">Selecione</option>
                      {BRAZILIAN_STATES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                    {form1.formState.errors.state && (
                      <p className="text-xs text-red-500">{form1.formState.errors.state.message}</p>
                    )}
                  </div>
                </div>
              </form>
            )}

            {/* ── STEP 2: Goal ───────────────────────────────────────── */}
            {step === 2 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Selecione um ou mais objetivos.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {GOALS.map((goal) => {
                    const selected = selectedGoals.has(goal.key)
                    return (
                      <button
                        key={goal.key}
                        type="button"
                        onClick={() => {
                          setSelectedGoals((prev) => {
                            const next = new Set(prev)
                            if (next.has(goal.key)) next.delete(goal.key)
                            else next.add(goal.key)
                            return next
                          })
                        }}
                        className={cn(
                          'flex items-start gap-3 rounded-xl border-2 p-3 text-left transition-all',
                          selected
                            ? 'border-brand-500 bg-brand-50 text-brand-800'
                            : 'border-gray-200 hover:border-gray-300',
                        )}
                      >
                        <span className="text-2xl leading-none">{goal.icon}</span>
                        <div>
                          <p className={cn('text-sm font-semibold', selected ? 'text-brand-800' : 'text-gray-800')}>
                            {goal.label}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">{goal.desc}</p>
                        </div>
                      </button>
                    )
                  })}
                </div>
                {selectedGoals.size === 0 && (
                  <p className="text-xs text-red-500">Selecione ao menos um objetivo.</p>
                )}
              </div>
            )}

            {/* ── STEP 3: Skill level ────────────────────────────────── */}
            {step === 3 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Como você avalia sua experiência de direção atual?
                </p>
                <div className="space-y-3">
                  {LEVELS.map((level) => {
                    const selected = selectedLevel === level.key
                    return (
                      <button
                        key={level.key}
                        type="button"
                        onClick={() => setSelectedLevel(level.key)}
                        className={cn(
                          'w-full flex items-center gap-4 rounded-xl border-2 p-4 text-left transition-all',
                          selected
                            ? 'border-brand-500 bg-brand-50'
                            : 'border-gray-200 hover:border-gray-300',
                        )}
                      >
                        <span className="text-3xl leading-none">{level.icon}</span>
                        <div className="flex-1">
                          <p className={cn('font-semibold', selected ? 'text-brand-800' : 'text-gray-800')}>
                            {level.label}
                          </p>
                          <p className="text-sm text-muted-foreground">{level.desc}</p>
                        </div>
                        <div className={cn(
                          'h-5 w-5 rounded-full border-2 flex items-center justify-center transition-all',
                          selected ? 'border-brand-500 bg-brand-500' : 'border-gray-300',
                        )}>
                          {selected && <div className="h-2 w-2 rounded-full bg-white" />}
                        </div>
                      </button>
                    )
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  Isso ajuda os instrutores a personalizarem as aulas para você.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between pb-10">
          <Button
            variant="outline"
            onClick={() => step > 1 && setStep((s) => s - 1)}
            disabled={step === 1 || submitting}
            className="gap-1.5"
          >
            <ChevronLeft className="h-4 w-4" />
            Voltar
          </Button>

          <Button
            onClick={handleNext}
            disabled={submitting}
            className="gap-1.5 bg-brand-600 hover:bg-brand-700"
          >
            {submitting
              ? 'Salvando...'
              : step === TOTAL_STEPS
              ? 'Concluir'
              : 'Próximo'}
            {!submitting && step < TOTAL_STEPS && <ChevronRight className="h-4 w-4" />}
            {!submitting && step === TOTAL_STEPS && <CheckCircle className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  )
}
